import { Link } from 'react-router-dom';
import { Card, Skeleton } from '@rackd/ui';
import { IconStar, IconDownload, IconClock } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import type { TopProgram } from '../api';

interface TopProgramsTableProps {
  programs: TopProgram[];
  loading?: boolean;
  onSortChange?: (sortBy: 'downloads' | 'rating' | 'recent') => void;
  currentSort?: 'downloads' | 'rating' | 'recent';
}

const sortOptions = [
  { value: 'downloads' as const, label: 'Downloads', icon: IconDownload },
  { value: 'rating' as const, label: 'Rating', icon: IconStar },
  { value: 'recent' as const, label: 'Recent', icon: IconClock },
];

export function TopProgramsTable({
  programs,
  loading,
  onSortChange,
  currentSort = 'downloads',
}: TopProgramsTableProps) {
  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[var(--text)]">Top Programs</h3>
        <div className="flex items-center gap-1 p-1 bg-[var(--background-tertiary)] rounded-lg">
          {sortOptions.map((option) => {
            const Icon = option.icon;
            const isActive = currentSort === option.value;
            return (
              <button
                key={option.value}
                onClick={() => onSortChange?.(option.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-[var(--background-secondary)] text-[var(--text)] shadow-sm'
                    : 'text-[var(--text-tertiary)] hover:text-[var(--text)]'
                }`}
              >
                <Icon size={14} />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {programs.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-[var(--text-tertiary)]">No programs found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider py-3 pr-4">
                  #
                </th>
                <th className="text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider py-3 pr-4">
                  Program
                </th>
                <th className="text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider py-3 pr-4">
                  Author
                </th>
                <th className="text-right text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider py-3 pr-4">
                  Downloads
                </th>
                <th className="text-right text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider py-3 pr-4">
                  Rating
                </th>
                <th className="text-right text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wider py-3">
                  Submitted
                </th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program, index) => (
                <tr
                  key={program.id}
                  className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--background-tertiary)]/50"
                >
                  <td className="py-3 pr-4">
                    <span className="text-sm font-medium text-[var(--text-tertiary)]">
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <Link
                      to={`/programs/${program.id}`}
                      className="text-sm font-medium text-[var(--text)] hover:text-[var(--accent-primary)]"
                    >
                      {program.name}
                    </Link>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm text-[var(--text-secondary)]">
                      {program.authorName}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <span className="text-sm font-medium text-[var(--text)]">
                      {program.downloads.toLocaleString()}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <IconStar size={14} className="text-[var(--pr-gold)]" />
                      <span className="text-sm font-medium text-[var(--text)]">
                        {program.rating.toFixed(1)}
                      </span>
                      <span className="text-xs text-[var(--text-tertiary)]">
                        ({program.ratingCount})
                      </span>
                    </div>
                  </td>
                  <td className="py-3 text-right">
                    <span className="text-sm text-[var(--text-tertiary)]">
                      {formatDistanceToNow(program.submittedAt, { addSuffix: true })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
